import React, { useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';

/**
 * Tracks the rendered height of the (fixed-position) wedding-code banner so the guest site can
 * reserve space for it at the top of the page — the same approach the smart-app banner uses. The
 * banner measures itself and reports its height here; `GuestSiteBody` adds it to the content's
 * top padding so the hero starts below the banner instead of being overlapped by it.
 */
type WeddingCodeBannerOffsetContextValue = Readonly<{
  weddingBannerHeight: number;
  setWeddingBannerHeight: (height: number) => void;
}>;

// Uses the shared createContext (not React's) for missing-provider observability; strict:false so
// reads outside a provider return undefined (handled by the no-op default in the hook below).
const [WeddingCodeBannerOffsetContextProvider, useWeddingCodeBannerOffsetContext] = createContext<WeddingCodeBannerOffsetContextValue>({
  name: 'WeddingCodeBannerOffset',
  strict: false,
});

export const WeddingCodeBannerOffsetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [weddingBannerHeight, setWeddingBannerHeight] = useState(0);

  const value = useMemo(() => {
    return { weddingBannerHeight, setWeddingBannerHeight };
  }, [weddingBannerHeight]);

  return <WeddingCodeBannerOffsetContextProvider value={value}>{children}</WeddingCodeBannerOffsetContextProvider>;
};

/**
 * Returns the current wedding-banner height and a setter. Safe to call without the provider —
 * height reads as 0 and the setter is a no-op — so consumers never need to guard for it.
 */
export const useWeddingCodeBannerOffset = (): WeddingCodeBannerOffsetContextValue => {
  return useWeddingCodeBannerOffsetContext() ?? { weddingBannerHeight: 0, setWeddingBannerHeight: () => undefined };
};
